// Pestaña Dispositivo: identidad y estado MDM del equipo financiado, más su bitácora real. La
// variante `collections` es la de Cobranza: identidad en rejilla e historial como línea de tiempo.
import type { ReactNode } from 'react';
import { AlertTriangle, Lock, LockOpen } from 'lucide-react';
import type { MdmOperationStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationStatusEnum';
import { cn } from '@/lib/cn';
import { wireEnum } from '@/lib/wireEnum';
import { wireLookup } from '@/lib/wireLookup';
import { formatDateMx, formatDateTimeMx, formatTimeMx } from '@/lib/dateHelpers';
import type {
    LoanDeviceFleetItem,
    LoanDeviceOperationItem,
    LoanDeviceOperationReason,
} from '@/api/services/loan-credit';
import {
    explainMissingEnrollmentDate,
    explainMissingHeartbeat,
    MDM_LOCK_MODE_SHORT_LABEL,
    MDM_OPERATION_LABEL,
    MDM_PROVIDER_LABEL,
    resolveOperationStatusPresentation,
} from '../../mdm/logic';
import type { MdmStatusTone } from '../../mdm/types';
import type { CreditFileBlock } from '../api/useCreditFile';
import { NO_DEVICE_MODEL_NOTE, NO_DEVICE_TELEMETRY_NOTE } from '../gaps';
import {
    MDM_EVENT_TONE,
    lockScheduleOf,
    mdmLockHealthOf,
    mdmLockSubline,
    mdmTimelineTally,
    recentMdmEvents,
    type CreditTone,
} from '../logic';
import { lockReadingOf } from '../../lockPhase';
import {
    CreditBlock,
    CreditCard,
    CreditEmptyNote,
    CreditGapMark,
    CreditKeyValue,
} from './CreditFileUi';

/** El corte del historial se dice en voz alta: callarlo se lee como «esto es todo lo que hubo». */
const TRUNCATED_TIMELINE_NOTE =
    'Aquí solo caben los eventos más recientes. La bitácora completa de este equipo está en Control MDM.';

/** La barra de color del evento y el rótulo de su tipo (`.fk-mdm-event` del diseño). */
const EVENT_TONE_CLASS: Record<CreditTone, { bar: string; label: string }> = {
    success: { bar: 'border-l-success', label: 'text-success' },
    danger: { bar: 'border-l-danger', label: 'text-danger' },
    warning: { bar: 'border-l-warning', label: 'text-warning' },
    info: { bar: 'border-l-info', label: 'text-info' },
    neutral: { bar: 'border-l-border', label: 'text-muted-foreground' },
};

/** Recuadro del candado y color del rótulo (`.fk-mdm-*` del diseño), uno por tono del estado. */
const WIDGET_TONE: Record<MdmStatusTone, { icon: string; label: string }> = {
    success: { icon: 'border-success-soft-border text-success', label: 'text-success' },
    danger: { icon: 'border-danger-soft-border text-danger', label: 'text-danger' },
    warning: { icon: 'border-warning-soft-border text-warning', label: 'text-warning' },
    info: { icon: 'border-info-soft-border text-info', label: 'text-info' },
    neutral: { icon: 'border-border text-muted-foreground', label: '' },
};

/** El motivo asentado en la bitácora, en minúsculas para ir detrás de «Motivo:». */
const OPERATION_REASON_LABEL: Record<LoanDeviceOperationReason, string> = {
    ADMIN_MANUAL: 'manual del administrador',
    FRAUD: 'fraude',
    INVESTIGATION: 'investigación',
    OVERDUE_T_PLUS_1: 'cuota vencida',
    PAYMENT_RECEIVED: 'pago recibido',
};

/** Un evento de la línea de tiempo: tipo con su color, desenlace, sello y cuerpo. */
function MdmTimelineEvent({ operation }: { operation: LoanDeviceOperationItem }) {
    const tone = EVENT_TONE_CLASS[wireLookup(MDM_EVENT_TONE, operation.operationType) ?? 'neutral'];
    // Quién ordenó el evento; sin correo en la bitácora el dato no se pinta.
    const actor = operation.actorEmail?.trim() ?? '';
    const outcome = resolveOperationStatusPresentation(
        wireEnum<MdmOperationStatusEnum>(operation.status),
    );

    return (
        <li className={cn('rounded-r border-l-[3px] bg-muted/15 px-2 py-1.5', tone.bar)}>
            <div className="mb-0.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                    <span className={cn('truncate text-[10.5px] font-bold', tone.label)}>
                        {wireLookup(MDM_OPERATION_LABEL, operation.operationType) ??
                            operation.operationType}
                    </span>
                    <span
                        title={outcome.title}
                        className={cn(
                            'inline-flex flex-none cursor-help items-center rounded border px-1 py-[1px] text-[9px] font-bold uppercase tracking-[0.03em]',
                            outcome.toneClass,
                        )}
                    >
                        {outcome.label}
                    </span>
                </span>
                <span className="flex-none text-[10px] tabular-nums text-muted-foreground">
                    {`${formatDateMx(operation.occurredAt)} · ${formatTimeMx(operation.occurredAt)}`}
                </span>
            </div>
            <p
                className={cn(
                    'text-[10.5px] leading-snug [overflow-wrap:anywhere]',
                    operation.errorMessage ? 'text-destructive' : 'text-muted-foreground',
                )}
            >
                {operation.errorMessage ?? outcome.title}
            </p>
            {operation.reason || operation.comment || actor ? (
                <p className="mt-0.5 text-[10.5px] leading-snug text-foreground">
                    {[
                        operation.reason
                            ? `Motivo: ${wireLookup(OPERATION_REASON_LABEL, operation.reason) ?? operation.reason}`
                            : null,
                        operation.comment ? `«${operation.comment}»` : null,
                        actor ? `Por ${actor}` : null,
                    ]
                        .filter((part): part is string => part !== null)
                        .map((part) => (
                            <span
                                key={part}
                                className="mr-1.5 inline-block max-w-full [overflow-wrap:anywhere]"
                            >
                                {part}
                            </span>
                        ))}
                </p>
            ) : null}
        </li>
    );
}

/** Franja del matiz del candado, con el patrón `info-strip` del mock en su tono. */
const STRIP_TONE: Record<'danger' | 'warning', string> = {
    danger: 'border-danger-soft-border bg-danger-soft text-danger',
    warning: 'border-warning-soft-border bg-warning-soft text-warning',
};

function DeviceStrip({
    tone,
    headline,
    children,
}: {
    tone: 'danger' | 'warning';
    headline: string;
    children: ReactNode;
}) {
    return (
        <div
            role={tone === 'danger' ? 'alert' : 'status'}
            className={cn(
                'mt-2 flex gap-2 rounded-md border px-3 py-2 text-[11px] leading-relaxed',
                STRIP_TONE[tone],
            )}
        >
            <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 [overflow-wrap:anywhere]">
                <strong className="font-bold">{headline}</strong> {children}
            </span>
        </div>
    );
}

/** Lo que matiza el candado: el desmentido o la falta de confirmación, y el último error. */
function MdmHealthStrips({ device }: { device: LoanDeviceFleetItem }) {
    const { warning } = mdmLockHealthOf(device);
    const error = device.lastError;
    return (
        <>
            {warning ? (
                <DeviceStrip tone={warning.tone} headline={warning.headline}>
                    {warning.body}
                </DeviceStrip>
            ) : null}
            {error ? (
                <DeviceStrip tone="danger" headline="Último error del proveedor:">
                    {error.message ? `${error.message} · ` : null}
                    <code className="font-mono text-[10.5px]">{error.code}</code>
                    {error.at ? (
                        <span className="opacity-70">{` (${formatDateTimeMx(error.at)})`}</span>
                    ) : null}
                </DeviceStrip>
            ) : null}
        </>
    );
}

/** El candado del diseño: cerrado solo con el candado confirmado, y nunca sano si se duda. */
function MdmLockWidget({ device }: { device: LoanDeviceFleetItem }) {
    const lock = lockScheduleOf(device);
    const presentation = mdmLockHealthOf(device);
    const tone = WIDGET_TONE[presentation.tone];
    const LockIcon = presentation.closedLock ? Lock : LockOpen;
    const subline = mdmLockSubline(
        device.mdmStatus,
        device.lockReason,
        lock.at ? formatDateTimeMx(lock.at) : undefined,
        lockReadingOf(device),
    );

    return (
        <div className="flex items-center gap-3.5 rounded-lg border border-border bg-muted/25 p-3">
            <span
                className={cn(
                    'inline-flex size-[58px] flex-none items-center justify-center rounded-xl border bg-card',
                    tone.icon,
                )}
            >
                <LockIcon className="size-[34px]" aria-hidden strokeWidth={2} />
            </span>
            <span className="min-w-0 flex-1">
                <span className={cn('block text-[16px] font-bold leading-[1.15]', tone.label)}>
                    {presentation.label}
                </span>
                <span className="mt-[3px] block text-[11px] text-muted-foreground">{subline}</span>
            </span>
        </div>
    );
}

export interface CreditDeviceTabProps {
    device: CreditFileBlock<LoanDeviceFleetItem | null>;
    operations: CreditFileBlock<LoanDeviceOperationItem[]>;
    /** Las órdenes MDM que la pantalla que monta la pestaña permite sobre el equipo. */
    actions?: ReactNode;
    /** `collections` = la de Cobranza: rejilla de identidad y línea de tiempo. */
    variant?: 'file' | 'collections';
}

export function CreditDeviceTab({
    device,
    operations,
    actions,
    variant = 'file',
}: CreditDeviceTabProps) {
    if (variant === 'collections') {
        return <CollectionsDeviceTab device={device} operations={operations} actions={actions} />;
    }
    const item = device.data;
    const timeline = recentMdmEvents(operations.data ?? []);
    const hardware = item ? [item.brand, item.model].filter(Boolean).join(' ').trim() : '';

    return (
        <>
            <CreditCard
                title="Identidad del dispositivo"
                footnote={item ? NO_DEVICE_TELEMETRY_NOTE : undefined}
            >
                <CreditBlock
                    isLoading={device.isLoading}
                    failure={device.failure}
                    subject="el equipo"
                    onRetry={device.retry}
                >
                    {item === null ? (
                        <CreditEmptyNote>
                            Este crédito no tiene dispositivo asociado.
                        </CreditEmptyNote>
                    ) : item ? (
                        <>
                            <CreditKeyValue
                                plain
                                label="Modelo"
                                value={hardware || undefined}
                                note={hardware ? undefined : NO_DEVICE_MODEL_NOTE}
                            />
                            <CreditKeyValue label="IMEI" value={item.imei} mono plain />
                            <CreditKeyValue
                                plain
                                label="Fecha de enrolamiento"
                                value={
                                    item.enrolledAt ? formatDateTimeMx(item.enrolledAt) : undefined
                                }
                                note={
                                    item.enrolledAt
                                        ? undefined
                                        : explainMissingEnrollmentDate(item.mdmStatus)
                                }
                            />
                            <CreditKeyValue
                                plain
                                label="Último check-in"
                                value={
                                    item.lastSeenAt ? formatDateTimeMx(item.lastSeenAt) : undefined
                                }
                                note={
                                    item.lastSeenAt
                                        ? undefined
                                        : explainMissingHeartbeat(item.mdmStatus)
                                }
                            />
                            <CreditKeyValue
                                label="SKU financiado"
                                value={item.productSku}
                                mono
                                plain
                            />
                            <CreditKeyValue
                                plain
                                label="Proveedor MDM"
                                value={
                                    wireLookup(MDM_PROVIDER_LABEL, item.mdmProvider) ??
                                    item.mdmProvider
                                }
                            />
                            <CreditKeyValue
                                plain
                                label="Modo de bloqueo"
                                value={
                                    wireLookup(MDM_LOCK_MODE_SHORT_LABEL, item.mdmLockMode) ??
                                    item.mdmLockMode
                                }
                            />
                        </>
                    ) : null}
                </CreditBlock>
            </CreditCard>

            {item ? (
                <CreditCard title="Estado MDM · aplicación del castigo">
                    <MdmLockWidget device={item} />
                    <MdmHealthStrips device={item} />
                    <div className="mt-2">
                        <CreditKeyValue
                            label="Operaciones en curso"
                            value={String(item.pendingOperations)}
                        />
                    </div>
                    {actions}
                </CreditCard>
            ) : actions ? (
                <CreditCard title="Estado MDM · aplicación del castigo">{actions}</CreditCard>
            ) : null}

            <CreditCard
                title="Historial MDM · últimos eventos"
                meta={timeline.events.length > 0 ? mdmTimelineTally(timeline) : undefined}
                footnote={timeline.hidden > 0 ? TRUNCATED_TIMELINE_NOTE : undefined}
            >
                <CreditBlock
                    isLoading={operations.isLoading}
                    failure={operations.failure}
                    subject="la bitácora del equipo"
                    onRetry={operations.retry}
                >
                    {timeline.events.length === 0 ? (
                        <CreditEmptyNote>Sin eventos.</CreditEmptyNote>
                    ) : (
                        <ul className="flex flex-col gap-1.5">
                            {timeline.events.map((operation) => (
                                <MdmTimelineEvent
                                    key={operation.operationId}
                                    operation={operation}
                                />
                            ))}
                        </ul>
                    )}
                </CreditBlock>
            </CreditCard>
        </>
    );
}

/** Fondo y texto del rótulo del evento en la línea de tiempo; el punto lleva solo el borde. */
const TIMELINE_TONE: Record<CreditTone, { badge: string; dot: string }> = {
    success: { badge: 'bg-success-soft text-success', dot: 'border-success' },
    danger: { badge: 'bg-danger-soft text-danger', dot: 'border-danger' },
    warning: { badge: 'bg-warning-soft text-warning', dot: 'border-warning' },
    info: { badge: 'bg-info-soft text-info', dot: 'border-info' },
    neutral: { badge: 'bg-muted text-muted-foreground', dot: 'border-muted-foreground' },
};

const GRID_LABEL = 'text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

/** Una celda de la rejilla de identidad; sin dato, el guion con su motivo. */
function GridValue({
    label,
    value,
    note,
    mono = false,
    title,
    className,
}: {
    label: string;
    value?: string;
    note?: string;
    mono?: boolean;
    className?: string;
    /** El valor completo al pasar el cursor, cuando la celda muestra una versión corta. */
    title?: string;
}) {
    return (
        <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
            <div className={GRID_LABEL}>{label}</div>
            <div
                className={cn(
                    'text-[11.5px] text-foreground',
                    mono ? 'break-all font-mono text-[11px]' : 'truncate',
                )}
                title={title ?? value}
            >
                {note ? <CreditGapMark note={note} /> : (value ?? '—')}
            </div>
        </div>
    );
}

/** La tarjeta de la variante de Cobranza: título, meta opcional, contenido y nota al pie. */
function DataCard({
    title,
    meta,
    footnote,
    children,
}: {
    title: string;
    meta?: ReactNode;
    footnote?: string;
    children: ReactNode;
}) {
    return (
        <section className="mb-2.5 rounded-md border border-border bg-card px-3 pb-3 pt-2.5">
            <header className="mb-2 flex flex-wrap items-baseline justify-between gap-x-2">
                <h3 className="text-[10.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                    {title}
                </h3>
                {meta ? (
                    <span className="text-[10px] tabular-nums text-muted-foreground">{meta}</span>
                ) : null}
            </header>
            {children}
            {footnote === undefined ? null : (
                <p className="mt-2 truncate text-[9.5px] text-muted-foreground" title={footnote}>
                    {footnote}
                </p>
            )}
        </section>
    );
}

/** Un evento de la bitácora como tarjeta con punto sobre la línea de tiempo. */
function TimelineEvent({ operation }: { operation: LoanDeviceOperationItem }) {
    const tone = TIMELINE_TONE[wireLookup(MDM_EVENT_TONE, operation.operationType) ?? 'neutral'];
    const actor = operation.actorEmail?.trim() ?? '';
    const outcome = resolveOperationStatusPresentation(
        wireEnum<MdmOperationStatusEnum>(operation.status),
    );
    const details = [
        operation.reason
            ? `Motivo: ${wireLookup(OPERATION_REASON_LABEL, operation.reason) ?? operation.reason}`
            : null,
        operation.comment ? `«${operation.comment}»` : null,
        actor ? `Por ${actor}` : null,
    ].filter((part): part is string => part !== null);

    return (
        <li className="relative mb-2 rounded-md border border-border bg-card px-2.5 py-[7px] last:mb-0">
            <span
                aria-hidden
                className={cn(
                    'absolute -left-[18px] top-2.5 size-2.5 rounded-full border-2 bg-card',
                    tone.dot,
                )}
            />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                <span
                    className={cn(
                        'rounded-[3px] px-[5px] py-px text-[8.5px] font-bold uppercase tracking-[0.04em]',
                        tone.badge,
                    )}
                >
                    {wireLookup(MDM_OPERATION_LABEL, operation.operationType) ??
                        operation.operationType}
                </span>
                <span className="tabular-nums">
                    {`${formatDateMx(operation.occurredAt)} · ${formatTimeMx(operation.occurredAt)}`}
                </span>
                <span
                    title={outcome.title}
                    className={cn(
                        'ml-auto cursor-help rounded border px-1 py-px text-[8.5px] font-bold uppercase tracking-[0.03em]',
                        outcome.toneClass,
                    )}
                >
                    {outcome.label}
                </span>
            </div>
            <p
                className={cn(
                    'mt-1 text-[11px] leading-snug [overflow-wrap:anywhere]',
                    operation.errorMessage ? 'text-destructive' : 'text-foreground',
                )}
            >
                {operation.errorMessage ?? outcome.title}
            </p>
            {details.length > 0 ? (
                <p className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground [overflow-wrap:anywhere]">
                    {details.join(' · ')}
                </p>
            ) : null}
        </li>
    );
}

/** Qué fue la última operación y qué día; `undefined` si el expediente no la trae. */
function lastOperationLabel(item: LoanDeviceFleetItem): string | undefined {
    if (!item.lastOperationType) return undefined;
    const label = wireLookup(MDM_OPERATION_LABEL, item.lastOperationType) ?? item.lastOperationType;
    return item.lastOperationAt ? `${label} · ${formatDateMx(item.lastOperationAt)}` : label;
}

function CollectionsDeviceTab({
    device,
    operations,
    actions,
}: Pick<CreditDeviceTabProps, 'device' | 'operations' | 'actions'>) {
    const item = device.data;
    const timeline = recentMdmEvents(operations.data ?? []);
    const hardware = item ? [item.brand, item.model].filter(Boolean).join(' ').trim() : '';

    return (
        <>
            <DataCard
                title="Identidad del dispositivo"
                footnote={item ? NO_DEVICE_TELEMETRY_NOTE : undefined}
            >
                <CreditBlock
                    isLoading={device.isLoading}
                    failure={device.failure}
                    subject="el equipo"
                    onRetry={device.retry}
                >
                    {item === null ? (
                        <CreditEmptyNote>
                            Este crédito no tiene dispositivo asociado.
                        </CreditEmptyNote>
                    ) : item ? (
                        <div className="grid grid-cols-3 gap-3">
                            <GridValue
                                label="Modelo"
                                value={hardware || undefined}
                                note={hardware ? undefined : NO_DEVICE_MODEL_NOTE}
                            />
                            <GridValue label="IMEI" value={item.imei} mono className="col-span-2" />
                            {item.imei2 ? (
                                <GridValue
                                    label="IMEI 2"
                                    value={item.imei2}
                                    mono
                                    className="col-span-2"
                                />
                            ) : null}
                            <GridValue label="SKU financiado" value={item.productSku} mono />
                            <GridValue
                                label="Fecha de enrolamiento"
                                value={item.enrolledAt ? formatDateMx(item.enrolledAt) : undefined}
                                title={
                                    item.enrolledAt ? formatDateTimeMx(item.enrolledAt) : undefined
                                }
                                note={
                                    item.enrolledAt
                                        ? undefined
                                        : explainMissingEnrollmentDate(item.mdmStatus)
                                }
                            />
                            <GridValue
                                label="Último check-in"
                                value={item.lastSeenAt ? formatDateMx(item.lastSeenAt) : undefined}
                                title={
                                    item.lastSeenAt ? formatDateTimeMx(item.lastSeenAt) : undefined
                                }
                                note={
                                    item.lastSeenAt
                                        ? undefined
                                        : explainMissingHeartbeat(item.mdmStatus)
                                }
                            />
                            <GridValue
                                label="Proveedor MDM"
                                value={
                                    wireLookup(MDM_PROVIDER_LABEL, item.mdmProvider) ??
                                    item.mdmProvider
                                }
                            />
                            <GridValue
                                label="Modo de bloqueo"
                                value={
                                    wireLookup(MDM_LOCK_MODE_SHORT_LABEL, item.mdmLockMode) ??
                                    item.mdmLockMode
                                }
                            />
                            <GridValue
                                label="Operaciones en curso"
                                value={String(item.pendingOperations)}
                            />
                            <GridValue
                                label="Última operación"
                                className="col-span-2"
                                value={lastOperationLabel(item)}
                                title={
                                    item.lastOperationAt
                                        ? formatDateTimeMx(item.lastOperationAt)
                                        : undefined
                                }
                            />
                        </div>
                    ) : null}
                </CreditBlock>
            </DataCard>

            {item || actions ? (
                <DataCard title="Estado MDM · aplicación del castigo">
                    {item ? (
                        <>
                            <MdmLockWidget device={item} />
                            <MdmHealthStrips device={item} />
                        </>
                    ) : null}
                    {actions}
                </DataCard>
            ) : null}

            <DataCard
                title="Historial MDM · últimos eventos"
                meta={timeline.events.length > 0 ? mdmTimelineTally(timeline) : undefined}
                footnote={timeline.hidden > 0 ? TRUNCATED_TIMELINE_NOTE : undefined}
            >
                <CreditBlock
                    isLoading={operations.isLoading}
                    failure={operations.failure}
                    subject="la bitácora del equipo"
                    onRetry={operations.retry}
                >
                    {timeline.events.length === 0 ? (
                        <CreditEmptyNote>Sin eventos.</CreditEmptyNote>
                    ) : (
                        <ul className="relative pl-5 before:absolute before:inset-y-1 before:left-1.5 before:w-0.5 before:bg-border">
                            {timeline.events.map((operation) => (
                                <TimelineEvent key={operation.operationId} operation={operation} />
                            ))}
                        </ul>
                    )}
                </CreditBlock>
            </DataCard>
        </>
    );
}
